import { useState, useRef, useEffect } from 'react'
import { BellIcon } from '../icons'
import { useTaskNotifications } from '../hooks/useTaskNotifications'
import { useNavigate } from 'react-router-dom'
import type { UINotification } from '../types'


export function TaskNotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, pushSupported, pushSubscribed, subscribePush } = useTaskNotifications()
  const [open, setOpen] = useState(false)
  const [showPushPrompt, setShowPushPrompt] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const formatTime = (ts: string | number | Date | undefined) => {
    if (!ts) return ''
    const d = new Date(ts)
    const now = new Date()
    const diff = (now.getTime() - d.getTime()) / 1000
    if (diff < 60) return 'agora'
    if (diff < 3600) return `${Math.floor(diff / 60)}min`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }

  const handleEnablePush = async () => {
    if (!('Notification' in window)) return
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      await subscribePush()
      setShowPushPrompt(false)
    }
  }

  const handleClick = (notif: UINotification) => {
    if (!notif.read_at) markAsRead(notif.id)
    if (notif._kind === 'chat' && notif.channel_id) {
      navigate(`/channel/${notif.channel_id}`)
    } else if (notif.task_id) {
      navigate(`/workboard?task=${notif.task_id}`)
    }
    setOpen(false)
  }

  return (
    <div className="task-notif-bell" ref={ref}>
      <button
        className="task-notif-bell-btn"
        onClick={() => setOpen(!open)}
        title="Notificações"
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span className="task-notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="task-notif-dropdown">
          <div className="task-notif-header">
            <span>Notificações</span>
            {unreadCount > 0 && (
              <button className="task-notif-mark-all" onClick={markAllAsRead}>
                Marcar todas como lidas
              </button>
            )}
          </div>

          {!pushSubscribed && pushSupported && (
            <div className="task-notif-push-prompt">
              <span>Ativar notificações no navegador?</span>
              <button className="btn-primary btn-sm" onClick={handleEnablePush}>
                Ativar
              </button>
            </div>
          )}

          <div className="task-notif-list">
            {notifications.length === 0 ? (
              <div className="task-notif-empty">
                <BellIcon size={32} />
                <p>Nenhuma notificação ainda</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`task-notif-item ${!n.read_at ? 'unread' : ''}`}
                  onClick={() => handleClick(n)}
                >
                  <div className="task-notif-item-icon">
                    {n._kind === 'chat' && (n.mention_type === 'all' ? '📢' : '💬')}
                    {n._kind !== 'chat' && n.action === 'assigned' && '👤'}
                    {n._kind !== 'chat' && n.action === 'unassigned' && '🚫'}
                    {n._kind !== 'chat' && n.action === 'status_changed' && '🔄'}
                    {n._kind !== 'chat' && n.action === 'updated' && '✏️'}
                    {n._kind !== 'chat' && n.action === 'created' && '✨'}
                    {n._kind !== 'chat' && n.action === 'deleted' && '🗑️'}
                    {n._kind !== 'chat' && n.action === 'commented' && '💬'}
                  </div>
                  <div className="task-notif-item-body">
                    <div className="task-notif-item-msg">
                      {n._kind === 'chat' ? `${n.sender_name} mencionou você em #${n.channel_name}` : n.message}
                    </div>
                    <div className="task-notif-item-time">{formatTime(n.created_at)}</div>
                  </div>
                  {!n.read_at && <div className="task-notif-item-dot" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
